import React, { useEffect, useState } from 'react';
import { pauseSensitiveCapture } from '../../utils/fullstory';
import { apiKeyService, ApiKey, ApiRequestLog, RequestFilters } from '../../services/apiKeyService';
import AuditTab from './AuditTab';

const date = (value: string | null) => (value ? new Date(value).toLocaleString() : 'Never');
const pretty = (value: string) => {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

export default function ApiKeysTab() {
  const [tab, setTab] = useState<'keys' | 'connect' | 'logs'>('keys');
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [name, setName] = useState('');
  const [days, setDays] = useState(30);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState<{
    secret: string;
    name: string;
    expires_at: string;
  } | null>(null);
  const [history, setHistory] = useState<number | null>(null);
  const [filters, setFilters] = useState<RequestFilters>({});
  const [logs, setLogs] = useState<ApiRequestLog[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    // Pause capture for the entire credential workflow, including creation/rotation responses.
    const resumeCapture = pauseSensitiveCapture();
    void apiKeyService
      .list()
      .then((r) => setKeys(r.data))
      .catch(() => setError('Could not load API keys.'));
    return resumeCapture;
  }, []);

  const manage = async (action: 'create' | 'rotate' | 'revoke', key?: ApiKey) => {
    setBusy(true);
    setError('');
    setRevealed(null);
    try {
      if (action === 'revoke') await apiKeyService.revoke(key!.id);
      else {
        const reply =
          action === 'create'
            ? await apiKeyService.create(name.trim(), days)
            : await apiKeyService.rotate(key!.id, days);
        setRevealed(reply.data);
        setName('');
      }
      setKeys((await apiKeyService.list()).data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const loadLogs = async (nextPage = 1, nextFilters = filters) => {
    setBusy(true);
    setError('');
    try {
      const reply = await apiKeyService.requests(
        Object.fromEntries(Object.entries(nextFilters).filter(([, v]) => v)),
        nextPage
      );
      setLogs(reply.data);
      setPage(nextPage);
      setHasMore(reply.pagination.hasMore);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const base = `${window.location.origin}/api`;
  const input = { padding: '0.5rem', marginRight: '0.5rem' };
  return (
    <div className="card fs-exclude">
      <h3>API Keys</h3>
      <p>
        Keys can read invoices, customers, and products. Only user-management administrators can
        manage them. Secrets expire within 30 days.
      </p>
      <div
        role="tablist"
        aria-label="API key administration"
        style={{ display: 'flex', gap: 8, marginBottom: 20 }}
      >
        {(['keys', 'connect', 'logs'] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            className={`btn ${tab === t ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => {
              setTab(t);
              setRevealed(null);
              if (t === 'logs') void loadLogs();
            }}
          >
            {{ keys: 'Keys', connect: 'How to connect', logs: 'Request logs' }[t]}
          </button>
        ))}
      </div>
      {error && (
        <div className="alert alert-error" role="alert">
          {error}
        </div>
      )}
      {revealed && (
        <div className="alert alert-info fs-exclude" role="status">
          <strong>{revealed.name}: copy this secret now. It will not be shown again.</strong>
          <p>
            Expires {date(revealed.expires_at)}. If rotated, the previous secret no longer works.
          </p>
          <input
            aria-label="New API secret"
            value={revealed.secret}
            readOnly
            autoComplete="off"
            spellCheck={false}
            style={{ width: '100%' }}
          />
          <button
            className="btn btn-secondary"
            onClick={() =>
              void navigator.clipboard
                .writeText(revealed.secret)
                .catch(() => setError('Copy failed. Select the secret and copy it manually.'))
            }
          >
            Copy secret
          </button>
          <button className="btn btn-secondary" onClick={() => setRevealed(null)}>
            Dismiss
          </button>
        </div>
      )}
      {tab === 'keys' && (
        <>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void manage('create');
            }}
            style={{ marginBottom: 20 }}
          >
            <label>
              Name{' '}
              <input
                style={input}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                required
              />
            </label>
            <label>
              Validity / rotation (days){' '}
              <input
                style={input}
                type="number"
                min={1}
                max={30}
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                required
              />
            </label>
            <button
              className="btn btn-primary"
              disabled={busy || !name.trim() || days < 1 || days > 30}
            >
              Create key
            </button>
          </form>
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status / version</th>
                  <th>Created</th>
                  <th>Issued</th>
                  <th>Expires</th>
                  <th>Last used</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((key) => (
                  <tr key={key.id}>
                    <td>{key.name}</td>
                    <td>
                      {key.status} / {key.secret_version}
                    </td>
                    <td>{date(key.created_at)}</td>
                    <td>{date(key.issued_at)}</td>
                    <td>{date(key.expires_at)}</td>
                    <td>{date(key.last_used_at)}</td>
                    <td>
                      <button
                        disabled={
                          busy ||
                          key.status === 'revoked' ||
                          !Number.isInteger(days) ||
                          days < 1 ||
                          days > 30
                        }
                        onClick={() => void manage('rotate', key)}
                      >
                        Rotate secret
                      </button>{' '}
                      <button
                        disabled={busy || key.status === 'revoked'}
                        onClick={() => void manage('revoke', key)}
                      >
                        Revoke
                      </button>{' '}
                      <button
                        onClick={() => {
                          const next = { keyId: String(key.id) };
                          setFilters(next);
                          setTab('logs');
                          void loadLogs(1, next);
                        }}
                      >
                        Requests
                      </button>{' '}
                      <button onClick={() => setHistory(history === key.id ? null : key.id)}>
                        History
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!keys.length && <p>No API keys yet.</p>}
          {history !== null && (
            <AuditTab
              key={history}
              onError={setError}
              initialFilters={{ table_name: 'api_keys', record_id: history }}
            />
          )}
        </>
      )}
      {tab === 'connect' && (
        <>
          <h4>How to connect</h4>
          <p>
            Base URL: <code>{base}</code>. Put the key in your AI tool's secure API credentials and
            send <code>Authorization: Bearer &lt;your-api-key&gt;</code>.
          </p>
          <p>
            Retrieve the endpoint descriptions, field definitions, business filters, and pagination
            instructions directly:
          </p>
          <pre>{`GET ${base}/docs?format=md\nAuthorization: Bearer <your-api-key>`}</pre>
          <p>
            Use <code>format=json</code> for OpenAPI JSON. Both formats require authentication.
          </p>
          <p>
            Available data: <code>GET /api/invoices</code>,{' '}
            <code>GET /api/invoices/&#123;id&#125;</code>, <code>GET /api/customers</code>,{' '}
            <code>GET /api/products</code>.
          </p>
          <pre>{`GET ${base}/invoices?startDate=2026-01-01&endDate=2026-01-31&pageSize=50\nAuthorization: Bearer <your-api-key>`}</pre>
          <p>
            Invoice eligibility is already applied. List invoices require both dates (maximum 366
            days). Rotate before expiry and update your tool with the replacement secret; the old
            secret stops working immediately.
          </p>
        </>
      )}
      {tab === 'logs' && (
        <>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void loadLogs();
            }}
          >
            <label>
              Key{' '}
              <select
                style={input}
                value={filters.keyId || ''}
                onChange={(e) => setFilters({ ...filters, keyId: e.target.value })}
              >
                <option value="">All keys</option>
                {keys.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.name}
                  </option>
                ))}
              </select>
            </label>
            {(['ip', 'endpoint', 'status', 'startDate', 'endDate'] as const).map((f) => (
              <label key={f}>
                {
                  {
                    ip: 'IP',
                    endpoint: 'URL',
                    status: 'Status',
                    startDate: 'From',
                    endDate: 'Through',
                  }[f]
                }{' '}
                <input
                  style={input}
                  type={f.endsWith('Date') ? 'date' : 'text'}
                  value={filters[f] || ''}
                  onChange={(e) => setFilters({ ...filters, [f]: e.target.value })}
                />
              </label>
            ))}
            <button className="btn btn-primary" disabled={busy}>
              Filter
            </button>
          </form>
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Key / version</th>
                  <th>IP</th>
                  <th>Request</th>
                  <th>Status / duration</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td>{date(log.created_at)}</td>
                    <td>
                      {log.key_name || 'Unverified'} / {log.secret_version ?? '—'}
                    </td>
                    <td>{log.ip_address}</td>
                    <td>
                      {log.method} {log.url}
                    </td>
                    <td>
                      {log.status} / {log.duration_ms} ms
                    </td>
                    <td>
                      <details>
                        <summary>View</summary>
                        <p>
                          {log.request_id} · {log.outcome} · {log.error_code}
                        </p>
                        <p>
                          Rows: {log.result_count ?? '—'} · Bytes: {log.response_bytes ?? '—'}
                        </p>
                        <pre>{pretty(log.parameters)}</pre>
                        <pre>{pretty(log.headers)}</pre>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!logs.length && <p>No matching requests.</p>}
          <button disabled={busy || page === 1} onClick={() => void loadLogs(page - 1)}>
            Previous
          </button>{' '}
          Page {page}{' '}
          <button disabled={busy || !hasMore} onClick={() => void loadLogs(page + 1)}>
            Next
          </button>
        </>
      )}
    </div>
  );
}
